"use client"

import { useState, useEffect, useCallback } from "react"

/**
 * Hook that tracks the actual visible viewport height on mobile devices.
 * This accounts for browser chrome (address bar, bottom nav) that CSS vh units don't handle.
 */
export function useViewportHeight() {
  const [viewportHeight, setViewportHeight] = useState<number | null>(null)

  const updateHeight = useCallback(() => {
    // window.innerHeight gives the actual visible viewport height
    // This excludes browser chrome (address bar, etc.)
    setViewportHeight(window.innerHeight)
  }, [])

  useEffect(() => {
    // Set initial height
    updateHeight()

    // Update on resize (including when browser chrome appears/disappears)
    window.addEventListener("resize", updateHeight)

    // Also update on orientation change
    window.addEventListener("orientationchange", updateHeight)

    // Some mobile browsers fire scroll events when chrome appears/disappears
    window.addEventListener("scroll", updateHeight)

    // Use visualViewport API if available (more accurate on mobile)
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", updateHeight)
      window.visualViewport.addEventListener("scroll", updateHeight)
    }

    return () => {
      window.removeEventListener("resize", updateHeight)
      window.removeEventListener("orientationchange", updateHeight)
      window.removeEventListener("scroll", updateHeight)
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", updateHeight)
        window.visualViewport.removeEventListener("scroll", updateHeight)
      }
    }
  }, [updateHeight])

  return viewportHeight
}
